import { useId, useMemo } from "react";

type Point = readonly [number, number];
type Stroke = readonly Point[];

interface LeafStamp {
  x: number;
  y: number;
  rotation: number;
  scale: number;
  opacity: number;
}

interface HiddenLeafGlyphProps {
  character: "义" | "信" | "志";
  visibleGroups: number[];
  guideGroups: number[];
  reveal?: boolean;
  className?: string;
  labelled?: boolean;
}

const GLYPH_STROKES: Record<HiddenLeafGlyphProps["character"], readonly (readonly Stroke[])[]> = {
  义: [
    [[[193, 72], [178, 110]]],
    [[[235, 120], [211, 161], [177, 214], [112, 304]]],
    [
      [[125, 132], [151, 177], [190, 226], [250, 296]],
      [[151, 178], [124, 198]],
    ],
  ],
  信: [
    [
      [[112, 92], [91, 139], [75, 205], [66, 307]],
      [[105, 111], [132, 147]],
    ],
    [
      [[208, 75], [214, 107]],
      [[151, 126], [271, 126]],
      [[163, 166], [259, 166]],
    ],
    [
      [[174, 205], [251, 205]],
      [[178, 238], [178, 307], [252, 307], [252, 238], [178, 238]],
      [[192, 271], [239, 271]],
    ],
  ],
  志: [
    [
      [[115, 94], [246, 94]],
      [[182, 72], [182, 176]],
    ],
    [
      [[104, 176], [258, 176]],
      [[148, 222], [137, 279]],
    ],
    [
      [[164, 237], [179, 279], [203, 302], [225, 300]],
      [[231, 235], [250, 265]],
      [[112, 252], [105, 286]],
    ],
  ],
};

const DECOY_LEAVES: LeafStamp[] = [
  { x: 47, y: 88, rotation: 24, scale: 1.05, opacity: 0.72 },
  { x: 70, y: 106, rotation: -28, scale: 0.88, opacity: 0.64 },
  { x: 94, y: 82, rotation: 42, scale: 0.92, opacity: 0.78 },
  { x: 273, y: 82, rotation: 148, scale: 1.02, opacity: 0.68 },
  { x: 298, y: 105, rotation: 202, scale: 0.9, opacity: 0.78 },
  { x: 319, y: 139, rotation: 155, scale: 0.82, opacity: 0.64 },
  { x: 39, y: 170, rotation: -36, scale: 0.9, opacity: 0.74 },
  { x: 70, y: 194, rotation: 31, scale: 0.84, opacity: 0.64 },
  { x: 292, y: 187, rotation: 142, scale: 1.08, opacity: 0.78 },
  { x: 323, y: 215, rotation: 198, scale: 0.84, opacity: 0.66 },
  { x: 43, y: 262, rotation: 28, scale: 1.02, opacity: 0.76 },
  { x: 72, y: 282, rotation: -31, scale: 0.86, opacity: 0.65 },
  { x: 286, y: 270, rotation: 151, scale: 0.95, opacity: 0.72 },
  { x: 322, y: 295, rotation: 202, scale: 0.82, opacity: 0.64 },
  { x: 42, y: 350, rotation: -22, scale: 0.96, opacity: 0.72 },
  { x: 78, y: 365, rotation: 34, scale: 0.82, opacity: 0.62 },
  { x: 112, y: 342, rotation: -35, scale: 0.74, opacity: 0.6 },
  { x: 251, y: 353, rotation: 28, scale: 0.82, opacity: 0.62 },
  { x: 286, y: 341, rotation: 151, scale: 0.98, opacity: 0.74 },
  { x: 318, y: 367, rotation: 202, scale: 0.86, opacity: 0.66 },
  { x: 147, y: 64, rotation: -18, scale: 0.72, opacity: 0.58 },
  { x: 229, y: 63, rotation: 198, scale: 0.76, opacity: 0.6 },
];

const LEAF_PATH = "M -15 0 C -7 -5.2, 7 -4.5, 16 0 C 7 4.2, -7 4.8, -15 0 Z";

function sampleGroup(strokes: readonly Stroke[], groupIndex: number): LeafStamp[] {
  const stamps: LeafStamp[] = [];
  strokes.forEach((stroke, strokeIndex) => {
    stroke.slice(0, -1).forEach((point, segmentIndex) => {
      const end = stroke[segmentIndex + 1];
      const dx = end[0] - point[0];
      const dy = end[1] - point[1];
      const length = Math.hypot(dx, dy);
      const steps = Math.max(1, Math.floor(length / 15));
      const tangent = (Math.atan2(dy, dx) * 180) / Math.PI;
      for (let index = 0; index < steps; index += 1) {
        const t = (index + 0.36) / steps;
        const direction = (index + strokeIndex + groupIndex) % 2 === 0 ? -1 : 1;
        stamps.push({
          x: point[0] + dx * t + direction * 1.5,
          y: point[1] + dy * t - direction * 1.5,
          rotation: tangent + direction * 19,
          scale: 0.86 + ((index + strokeIndex) % 3) * 0.07,
          opacity: 0.78 + ((index + groupIndex) % 4) * 0.05,
        });
      }
    });
  });
  return stamps;
}

function strokePath(stroke: Stroke): string {
  return stroke.map((point, index) => `${index === 0 ? "M" : "L"} ${point[0]} ${point[1]}`).join(" ");
}

export function HiddenLeafGlyph({
  character,
  visibleGroups,
  guideGroups,
  reveal = false,
  className = "",
  labelled = false,
}: HiddenLeafGlyphProps) {
  const rawId = useId();
  const filterId = `hidden-leaf-${rawId.replace(/:/g, "")}`;
  const groups = useMemo(
    () => GLYPH_STROKES[character].map((strokes, index) => sampleGroup(strokes, index)),
    [character],
  );
  const visible = new Set(visibleGroups);
  const guided = new Set(guideGroups);

  return (
    <svg
      className={`hidden-leaf-glyph ${reveal ? "is-revealed" : ""} ${className}`}
      viewBox="0 0 360 420"
      role={labelled ? "img" : undefined}
      aria-label={labelled ? `竹叶藏着“${character}”字` : undefined}
      aria-hidden={labelled ? undefined : true}
    >
      <defs>
        <filter id={filterId} x="-8%" y="-8%" width="116%" height="116%">
          <feTurbulence type="fractalNoise" baseFrequency="0.02 0.08" numOctaves="1" seed={character.codePointAt(0) ?? 1} result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="0.85" />
        </filter>
      </defs>

      <g className="hidden-glyph-branches">
        <path d="M 20 390 Q 92 330 103 82" />
        <path d="M 72 370 Q 232 355 338 202" />
        <path d="M 286 24 Q 301 118 332 244" />
      </g>

      <g className="hidden-glyph-decoys" filter={`url(#${filterId})`}>
        {DECOY_LEAVES.map((leaf, index) => (
          <path
            key={index}
            className="ink-leaf"
            d={LEAF_PATH}
            opacity={leaf.opacity}
            transform={`translate(${leaf.x} ${leaf.y}) rotate(${leaf.rotation}) scale(${leaf.scale})`}
          />
        ))}
      </g>

      <g className="hidden-glyph-guides">
        {GLYPH_STROKES[character].map((strokes, groupIndex) => (
          <g key={groupIndex} className={guided.has(groupIndex) ? "is-visible" : ""}>
            {strokes.map((stroke, strokeIndex) => <path key={strokeIndex} d={strokePath(stroke)} />)}
          </g>
        ))}
      </g>

      <text x="180" y="310" textAnchor="middle" className="hidden-glyph-reveal-label">
        {character}
      </text>

      <g className="hidden-glyph-letter" filter={`url(#${filterId})`}>
        {groups.map((leaves, groupIndex) => (
          <g key={groupIndex} data-leaf-group={groupIndex} className={visible.has(groupIndex) ? "is-visible" : ""}>
            {leaves.map((leaf, index) => (
              <path
                key={index}
                className="ink-leaf"
                d={LEAF_PATH}
                opacity={leaf.opacity}
                transform={`translate(${leaf.x} ${leaf.y}) rotate(${leaf.rotation}) scale(${leaf.scale})`}
              />
            ))}
          </g>
        ))}
      </g>
    </svg>
  );
}

export function LeafClusterPiece({ kind }: { kind: "one" | "ge" | "jie" }) {
  const leaves =
    kind === "one"
      ? [{ x: 42, y: 24, rotation: -18 }]
      : kind === "ge"
        ? [
            { x: 28, y: 20, rotation: -32 },
            { x: 46, y: 25, rotation: 25 },
            { x: 62, y: 18, rotation: -18 },
          ]
        : [
            { x: 25, y: 20, rotation: -36 },
            { x: 42, y: 28, rotation: 30 },
            { x: 58, y: 18, rotation: -22 },
            { x: 69, y: 30, rotation: 36 },
          ];
  return (
    <svg className="leaf-piece-art" viewBox="0 0 88 48" aria-hidden="true">
      <path className="leaf-piece-twig" d="M 12 29 Q 44 26 77 22" />
      {leaves.map((leaf, index) => (
        <path key={index} className="ink-leaf" d={LEAF_PATH} transform={`translate(${leaf.x} ${leaf.y}) rotate(${leaf.rotation}) scale(.72)`} />
      ))}
    </svg>
  );
}
